import React,{useState,useEffect,useRef} from 'react'
import { Popover } from 'react-tiny-popover'   
import {SelectionGroup,SelectionItem} from "../Selection/Selection"
import {PurpleButton} from "../Buttons/PurpleButton"
import {NumberSpin} from "../Spin/NumberSpin"
import {SearchNormalIcon,PinIcon} from "../../AssetComponents/Images"
import { useDispatch,useSelector } from 'react-redux'
import {setWho,setReservationPeriod} from "../../ReduxSlices/ReservationSlice"
import { api } from '../functions/Apiwithouttoken'
import { createPortal } from 'react-dom';
import { MobileCalendar } from './MobileCalendar'
import { DesktopCalendar } from './DesktopCalendar'
import { getMinBookingDate, getDefaultStartDate } from '../utils/DateValidation';

export const RentalSearch = ({onSearch,setPinLocation,onReset})=>{
  // Separate states for desktop and mobile
  const [desktopSearchPop, setDesktopSearchPop] = useState(false);
  const [mobileSearchPop, setMobileSearchPop] = useState(false);
  const [showMobileCalendar, setShowMobileCalendar] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [searchItem, setSearchItem] = useState(-1);
  const [predictList, setPredictList] = useState([]);
  const [addressInput, setAddressInput] = useState("");
  const [isWhereFocused, setIsWhereFocused] = useState(false);
  const whereInputRef = useRef(null);
  // Initialize period with default start date (tomorrow if after 11:30 AM, today otherwise)
  const getInitialPeriod = () => {
    const defaultStartDate = getDefaultStartDate();
    return [
      {
        startDate: defaultStartDate,
        endDate: defaultStartDate,
        key: "selection",
        color: "#5b1deeaa",
      },
    ];
  };

  const [period, setPeriod] = useState(getInitialPeriod());
  const dispatch = useDispatch();
  const reservation_period = useSelector((state) => state.reservation.period);
  const who = useSelector((state) => state.reservation.who);
  const guests = useSelector((state) => state.reservation.guests);

  // Updated to use the new Google Places API
  const geocoder = new window.google.maps.Geocoder();

  const getAddressList = async (input) => {
    if (!input) {
      setPredictList([]);
      return;
    }
    
    try {
      const service = new window.google.maps.places.AutocompleteService();
      
      const request = {
        input: input,
        // types: ['(cities)'],
        // componentRestrictions: { country: ['us', 'in', 'gb', 'uae'] },
        sessionToken: new window.google.maps.places.AutocompleteSessionToken()
      };

      service.getPlacePredictions(
        request,
        function (predictions, status) {
          if (
            status !== window.google.maps.places.PlacesServiceStatus.OK ||
            !predictions
          ) {
            setPredictList([]);
            return;
          }
          setPredictList(predictions);
        }
      );
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
      setPredictList([]);
    }
  };

  const handleSelectFromAddressList = async (place_id, description) => {
    try {
      // Clear predictions immediately when selection is made
      setPredictList([]);
      
      const res = await geocoder.geocode({ placeId: place_id });
      if (res.results[0]) {
        const location = {
          lat: res.results[0].geometry.location.lat(),
          lng: res.results[0].geometry.location.lng(),
          address: description
        };
        setAddressInput(description);
        // Keep input focused for easy editing after selection
        setTimeout(() => {
          if (whereInputRef.current) {
            whereInputRef.current.focus();
            // Place cursor at end for easy editing
            whereInputRef.current.setSelectionRange(description.length, description.length);
          }
        }, 100);
        return location;
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      // Try geocoding by address as fallback
      try {
        const res = await geocoder.geocode({ address: description });
        if (res.results[0]) {
          const location = {
            lat: res.results[0].geometry.location.lat(),
            lng: res.results[0].geometry.location.lng(),
            address: description
          };
          setAddressInput(description);
          // Keep input focused for easy editing after selection
          setTimeout(() => {
            if (whereInputRef.current) {
              whereInputRef.current.focus();
              whereInputRef.current.setSelectionRange(description.length, description.length);
            }
          }, 100);
          return location;
        }
      } catch (fallbackError) {
        console.error('Fallback geocoding also failed:', fallbackError);
      }
    }
    return null;
  };

  // Enhanced handleSelectFromAddressList specifically for mobile
  const handleMobileSelectFromAddressList = async (place_id, description) => {
    console.log('Mobile selection made:', description);
    
    // Immediately clear predictions and set address
    setPredictList([]);
    setAddressInput(description);
    
    try {
      const res = await geocoder.geocode({ placeId: place_id });
      if (res.results[0]) {
        const location = {
          lat: res.results[0].geometry.location.lat(),
          lng: res.results[0].geometry.location.lng(),
          address: description
        };
        console.log('Mobile geocoding successful:', location);
        // Keep input focused and cursor at end for easy editing
        setTimeout(() => {
          const mobileInput = document.querySelector('input[placeholder="Search destinations"]');
          if (mobileInput) {
            mobileInput.focus();
            mobileInput.setSelectionRange(description.length, description.length);
          }
        }, 100);
        return location;
      }
    } catch (error) {
      console.error('Mobile geocoding error:', error);
      // Try geocoding by address as fallback
      try {
        const res = await geocoder.geocode({ address: description });
        if (res.results[0]) {
          const location = {
            lat: res.results[0].geometry.location.lat(),
            lng: res.results[0].geometry.location.lng(),
            address: description
          };
          console.log('Mobile fallback geocoding successful:', location);
          // Keep input focused and cursor at end for easy editing
          setTimeout(() => {
            const mobileInput = document.querySelector('input[placeholder="Search destinations"]');
            if (mobileInput) {
              mobileInput.focus();
              mobileInput.setSelectionRange(description.length, description.length);
            }
          }, 100);
          return location;
        }
      } catch (fallbackError) {
        console.error('Mobile fallback geocoding also failed:', fallbackError);
      }
    }
    return null;
  };
  
  // Debounce with shorter delay for smoother typing experience
  useEffect(() => {
    if (!addressInput) {
      setPredictList([]);
      return;
    }
    
    const timeoutId = setTimeout(() => {
      getAddressList(addressInput);
    }, 150); // Reduced from 300ms to 150ms for more responsive feel

    return () => clearTimeout(timeoutId);
  }, [addressInput]);

  // Auto-focus input when "Where" is selected (but don't auto-select text)
  useEffect(() => {
    if (searchItem === 0 && desktopSearchPop && whereInputRef.current) {
      // Only focus, don't select text to allow smooth typing
      const focusInput = () => {
        if (whereInputRef.current && document.activeElement !== whereInputRef.current) {
          whereInputRef.current.focus();
          // Don't select text - let user type naturally
        }
      };
      
      // Try after a short delay in case input isn't rendered yet
      const timeout = setTimeout(focusInput, 100);
      
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [searchItem, desktopSearchPop]);

  useEffect(() => {
    dispatch(
      setReservationPeriod({
        start: Math.floor(new Date(period[0].startDate).getTime() / 1000),
        end: Math.floor(new Date(period[0].endDate).getTime() / 1000),
      })
    );  
  }, [period, dispatch]);

    const handleSearch = async () => {
    setHasSearched(true);
    let locationData = null;
    let countryName = null;
    
    try {
      if (addressInput) {
        try {
          const res = await geocoder.geocode({ address: addressInput });
          if (res.results[0]) {
            locationData = {
              lat: res.results[0].geometry.location.lat(),
              lng: res.results[0].geometry.location.lng()
            };
            
            // Extract country from address components
            const addressComponents = res.results[0].address_components || [];
            const countryComponent = addressComponents.find(
              component => component.types.includes('country')
            );
            
            if (countryComponent) {
              countryName = countryComponent.long_name;
              console.log("Extracted country:", countryName);
            }
            
            console.log("Geocoded location:", locationData);
          }
        } catch (geoError) {
          console.error("Geocoding error:", geoError);
        }
      }
    
      const totalGuests = (who.adults || 2) + (who.children || 0) + (who.infants || 0) + (who.pets || 0);
    
      const searchParams = {
        location: locationData,
        country: countryName, // Send country name to backend
        period: {
          startDate: period[0].startDate.toISOString().split('T')[0],
          endDate: period[0].endDate.toISOString().split('T')[0]
        },
        guests: totalGuests,
        radius: 15000
      };
    
      console.log('Sending search request with params:', searchParams);
      
            const response = await api("property/searchProperties", searchParams);    

      // Handle different response types
      if (Array.isArray(response)) {
        console.log(`Search returned ${response.length} results`);
        // If no results and no location was specified, pass metadata about location
        if (response.length === 0 && !locationData) {
          onSearch({ hasLocation: false, results: [] });
        } else {
          onSearch(response);
        }
      } else if (response && typeof response === 'object') {
        // Handle special response format for country-not-available
        if (response.countryNotAvailable === true) {
          // Return special object to indicate country not available
          onSearch({ countryNotAvailable: true, country: response.country || countryName });                                                                    
        } else if (response.properties && Array.isArray(response.properties)) { 
          console.log(`Search returned ${response.properties.length} results`); 
          // If no results and no location was specified, pass metadata about location
          if (response.properties.length === 0 && !locationData) {
            onSearch({ hasLocation: false, results: [] });
          } else {
            onSearch(response.properties);
          }
        } else {
          console.error("Unexpected response format:", response);
          // If no location was specified, pass metadata
          if (!locationData) {
            onSearch({ hasLocation: false, results: [] });
          } else {
            onSearch([]);
          }
        }
      } else {
        console.error("Unexpected response format:", response);
        // If no location was specified, pass metadata
        if (!locationData) {
          onSearch({ hasLocation: false, results: [] });
        } else {
          onSearch([]);
        }
      }
    } catch (error) {
      console.error("Search failed:", error);
      alert("Search failed: " + error.message);
      // If no location was specified, pass metadata
      if (!locationData) {
        onSearch({ hasLocation: false, results: [] });
      } else {
        onSearch([]);
      }
    }
  };

  const handleMobileSearch = () => {
    handleSearch();
    setMobileSearchPop(false);
  };

  const handleDesktopItemClick = (item) => {
    setSearchItem(item);
    setDesktopSearchPop(true);
    
    // Auto-focus input when "Where" is clicked for smooth editing
    if (item === 0) {
      // Use requestAnimationFrame to ensure the input is rendered before focusing
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (whereInputRef.current) {
            whereInputRef.current.focus();
            // Select all text if there's existing text for easy editing
            if (addressInput) {
              whereInputRef.current.select();
            }
          }
        }, 100);
      });
    }
  };

  const handleCloseMobileModal = () => {
    // console.log('Closing mobile modal');
    setMobileSearchPop(false);
    setSearchItem(-1);
    setPredictList([]);
  };

  const handleMobileDateSelect = (dateRange) => {
    setPeriod([{
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      key: "selection",
      color: "#5b1deeaa",
    }]);
    setShowMobileCalendar(false);
  };

  const handleDesktopDateSelect = (dateRange) => {
    setPeriod([{
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      key: "selection",
      color: "#5b1deeaa",
    }]);
    setDesktopSearchPop(false);
    setSearchItem(-1);
  };

  const handleMobileCalendarClose = () => {
    setShowMobileCalendar(false);
  };



  const handleMobileClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // console.log('Mobile search clicked! Opening modal...');
    setMobileSearchPop(true);
  };

  // // Debug effects
  // useEffect(() => {
  //   console.log('mobileSearchPop state changed to:', mobileSearchPop);
  // }, [mobileSearchPop]);

  // useEffect(() => {
  //   console.log('desktopSearchPop state changed to:', desktopSearchPop);
  // }, [desktopSearchPop]);

  // // Debug effect for predictList
  // useEffect(() => {
  //   console.log('predictList changed:', predictList.length, 'items');
  // }, [predictList]);

  // Prevent body scroll when mobile modal is open
  useEffect(() => {
    if (mobileSearchPop) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [mobileSearchPop]);

  return (
    <>
      {/* Desktop Search Bar */}
      <div className="hidden lg:block">
        <Popover
          isOpen={desktopSearchPop}
          positions={["bottom"]}
          onClickOutside={() => {
            setDesktopSearchPop(false);
            setSearchItem(-1);
            setPredictList([]);
          }}
                     containerStyle={{ zIndex: 9999999 }}
          content={
                         <div className={`rent-search-popover-content mt-[10px] z-[9999999] w-max h-max ${(searchItem === 1 || searchItem === 2) ? '' : 'bg-white rounded-[10px]'}`}>
              {searchItem === 0 && predictList?.length > 0 && (
                <div className="max-h-[300px] overflow-y-auto">
                  {predictList.map((address) => (
                    <div
                      key={address.place_id}
                      onClick={() => handleSelectFromAddressList(address.place_id, address.description)}
                      className="w-[400px] bg-white flex gap-[10px] items-center cursor-pointer overflow-hidden min-h-[50px] p-[12px] rounded-[8px] hover:bg-gray-100 transition-colors duration-150 ease-out border-b border-gray-100 last:border-b-0"
                    >
                      <PinIcon className="flex-shrink-0" />
                      <div className="w-full truncate text-gray-900">{address.description}</div>
                    </div>
                  ))}
                </div>
              )}
              {(searchItem === 1 || searchItem === 2) && (
                <div>
                  <DesktopCalendar
                    selectedDates={{
                      startDate: period[0].startDate,
                      endDate: period[0].endDate
                    }}
                    onDateSelect={handleDesktopDateSelect}
                    onClose={() => setSearchItem(-1)}
                    minDate={getMinBookingDate()}
                    title=""
                  />
                </div>
              )}
              {searchItem === 3 && (
                <div className="w-[340px] p-[20px] space-y-[10px]">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold">Adults</div>
                      <div className="text-[#959595]">Age 13 or above</div>
                    </div>
                    <NumberSpin
                      value={who.adults || 1}
                      onChange={(value) =>
                        dispatch(setWho({ ...who, adults: value }))
                      }
                      min={1}
                    />
                  </div>
                  <div className="flex items-center justify-between ">
                    <div>
                      <div className="font-semibold">Children</div>
                      <div className="text-[#959595]">Age 2-12</div>
                    </div>
                    <div className="flex items-center">
                      <NumberSpin
                        value={who.children || 0}
                        onChange={(value) =>
                          dispatch(
                            setWho({
                              ...who,
                              children: value,
                            })
                          )
                        }
                        min={0}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between ">
                    <div>
                      <div className="font-semibold">Infants</div>
                      <div className="text-[#959595]">Under 2</div>
                    </div>
                    <div className="flex items-center">
                      <NumberSpin
                        value={who.infants || 0}
                        onChange={(value) =>
                          dispatch(
                            setWho({
                              ...who,
                              infants: value,
                            })
                          )
                        }
                        min={0}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between ">
                    <div>
                      <div className="font-semibold">Pets</div>
                      <div className="text-[#959595]">Pet friendly only</div>
                    </div>
                    <div className="flex items-center">
                      <NumberSpin
                        value={who.pets || 0}
                        onChange={(value) =>
                          dispatch(
                            setWho({
                              ...who,
                              pets: value,
                            })
                          )
                        }
                        min={0}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          }
        >
          <div className="flex items-center h-max w-max bg-white border-[2px] border-[#e3e3e3] rounded-[14px] transition-all duration-200 ease-out overflow-hidden">
            <SelectionGroup
              SelectedItemMask="rounded-[10px] bg-[#e3e3e3] shadow-none"
              defaultItem={searchItem}
              className="gap-0 flex items-center w-max rounded-[14px]"
            >
              <SelectionItem
                UnselectedItem={
                  <div
                    className="cursor-pointer hover:bg-[#f6f6f6] space-y-[4px] py-[8px] px-[20px] bg-white rounded-l-[10px] transition-all duration-200 ease-out flex items-center gap-2 min-w-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!(e.target.tagName === 'BUTTON' || e.target.closest('button'))) {
                        handleDesktopItemClick(0);
                        setTimeout(() => {
                          if (whereInputRef.current) whereInputRef.current.focus();
                        }, 50);
                      }
                    }}
                  >
                    <div className="flex-1 min-w-0 space-y-[4px]">
                      <div className="text-[16px] font-medium">Where</div>
                      <div className="flex items-center gap-2">
                        <input
                          ref={whereInputRef}
                          placeholder="Search destinations"
                          className="outline-none bg-transparent font-normal w-full transition-all duration-200 text-[#666666] placeholder:text-[#666666] min-w-0"
                          value={addressInput}
                          onChange={(e) => {
                            setAddressInput(e.target.value);
                            if (!desktopSearchPop) handleDesktopItemClick(0);
                          }}
                          onFocus={(e) => {
                            e.stopPropagation();
                            setIsWhereFocused(true);
                            handleDesktopItemClick(0);
                            setTimeout(() => {
                              if (whereInputRef.current && addressInput) {
                                const length = addressInput.length;
                                whereInputRef.current.setSelectionRange(length, length);
                              }
                            }, 0);
                          }}
                          onBlur={() => setIsWhereFocused(false)}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            if (!whereInputRef.current?.matches(':focus')) handleDesktopItemClick(0);
                          }}
                          style={{ caretColor: '#5b1dee', minWidth: '120px' }}
                        />
                      </div>
                    </div>
                  </div>
                }
                SelectedItem={
                  <div 
                    className="cursor-pointer hover:bg-[#f6f6f6] space-y-[4px] py-[8px] px-[20px] bg-[#e3e3e3] rounded-l-[10px] transition-all duration-200 ease-out flex items-center gap-2 min-w-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!(e.target.tagName === 'BUTTON' || e.target.closest('button')) && whereInputRef.current) {
                        whereInputRef.current.focus();
                      }
                    }}
                  >
                    <div className="flex-1 min-w-0 space-y-[4px]">
                      <div className="text-[16px] font-medium">Where</div>
                      <div className="flex items-center gap-2">
                        <input
                          ref={whereInputRef}
                          placeholder="Search destinations"
                          className="outline-none bg-transparent font-normal w-full transition-all duration-200 pointer-events-auto text-[#666666] placeholder:text-[#666666] min-w-0"
                          value={addressInput}
                          onChange={(e) => setAddressInput(e.target.value)}
                          onFocus={() => {
                            setIsWhereFocused(true);
                            setTimeout(() => {
                              if (whereInputRef.current && addressInput) {
                                const length = addressInput.length;
                                whereInputRef.current.setSelectionRange(length, length);
                              }
                            }, 0);
                          }}
                          onBlur={() => setIsWhereFocused(false)}
                          onMouseDown={(e) => e.stopPropagation()}
                          style={{ caretColor: '#5b1dee', minWidth: '120px' }}
                        />
                      </div>
                    </div>
                  </div>
                }
              />
              <SelectionItem
                UnselectedItem={
                  <div
                    onClick={() => handleDesktopItemClick(1)}
                    className="space-y-[4px] hover:bg-[#f6f6f6] py-[8px] px-[20px] bg-white cursor-pointer transition-all duration-200 ease-out"
                  >
                    <div className="font-medium">Check in</div>
                    <div className="text-[#666666] font-normal truncate">
                      {reservation_period.start
                        ? new Date(
                            reservation_period.start * 1000
                          ).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                        : "Add Date"}
                    </div>
                  </div>
                }
                SelectedItem={
                  <div 
                    className="space-y-[4px] hover:bg-[#f6f6f6] py-[8px] px-[20px] bg-[#e3e3e3] cursor-pointer transition-all duration-200 ease-out"
                    style={{ boxShadow: 'none' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Ensure calendar opens when clicking on selected item
                      handleDesktopItemClick(1);
                    }}
                  >
                    <div className="font-medium">Check in</div>
                    <div className="text-[#666666] font-normal truncate">
                      {reservation_period.start
                        ? new Date(
                            reservation_period.start * 1000
                          ).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                        : "Add Date"}
                    </div>
                  </div>
                }
              />
              <SelectionItem
                UnselectedItem={
                  <div
                    onClick={() => handleDesktopItemClick(2)}
                    className="space-y-[4px] hover:bg-[#f6f6f6] py-[8px] px-[20px] bg-white cursor-pointer transition-all duration-200 ease-out"
                  >
                    <div className="font-medium">Check out</div>
                    <div className="text-[#666666] font-normal truncate">
                      {reservation_period.end
                        ? new Date(
                            reservation_period.end * 1000
                          ).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                        : "Add Date"}
                    </div>
                  </div>
                }
                SelectedItem={
                  <div 
                    className="space-y-[4px] hover:bg-[#f6f6f6] py-[8px] px-[20px] bg-[#e3e3e3] cursor-pointer transition-all duration-200 ease-out"
                    style={{ boxShadow: 'none' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Ensure calendar opens when clicking on selected item
                      handleDesktopItemClick(2);
                    }}
                  >
                    <div className="font-medium">Check out</div>
                    <div className="text-[#666666] font-normal truncate">
                      {reservation_period.end
                        ? new Date(
                            reservation_period.end * 1000
                          ).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                        : "Add Date"}
                    </div>
                  </div>
                }
              />
              <SelectionItem
                UnselectedItem={
                  <div
                    onClick={() => handleDesktopItemClick(3)}
                    className="space-y-[4px] hover:bg-[#f6f6f6] py-[8px] px-[20px] bg-white w-[120px] rounded-r-[10px] cursor-pointer transition-all duration-200 ease-out"
                  >
                    <div className="font-medium">Who</div>
                    <div className="text-[#666666] font-normal truncate">
                      {guests ? guests : "Add Guests"}
                    </div>
                  </div>
                }
                SelectedItem={
                  <div 
                    className="space-y-[4px] hover:bg-[#f6f6f6] py-[8px] px-[20px] bg-[#e3e3e3] rounded-r-[10px] w-[120px] cursor-pointer transition-all duration-200 ease-out"
                    style={{ boxShadow: 'none' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Ensure guests popover opens when clicking on selected item
                      handleDesktopItemClick(3);
                    }}
                  >
                    <div className="font-medium">Who</div>
                    <div className="text-[#666666] font-normal truncate">
                      {guests ? guests : "Add Guests"}
                    </div>
                  </div>
                }
              />
            </SelectionGroup>
            <div className="relative z-10 pl-1 pr-2 flex items-center bg-white shrink-0">
              <PurpleButton
                text={
                  <div className="flex items-center justify-center gap-[10px]">
                    <SearchNormalIcon />
                    <div className="font-normal">Search</div>
                  </div>
                }
                onClick={handleSearch}
                className="w-auto px-6"
              />
            </div>
          </div>
        </Popover>
      </div>

      {/* Mobile Search Bar */}
      <div className="lg:hidden w-full">
        <div
          onClick={handleMobileClick}
          className="w-full bg-white rounded-[14px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] border-[2px] border-[#e3e3e3] p-4 cursor-pointer touch-manipulation active:bg-gray-50"
          style={{ 
            userSelect: 'none',
            touchAction: 'manipulation'
          }}
        >
          <div className="flex items-center gap-3">
            <SearchNormalIcon />
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900">
                {addressInput || "Where to?"}
              </div>
              <div className="text-xs text-[#666666]">
                {period[0].startDate && period[0].endDate && period[0].startDate.toDateString() !== new Date().toDateString()
                  ? `${period[0].startDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${period[0].endDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
                  : "Any week"} • {guests || "Add guests"}
              </div>
            </div>
            <div className="w-8 h-8 bg-gradient-to-b from-[#5b1deec0] to-[#5b1dee] rounded-full flex items-center justify-center shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
              <SearchNormalIcon className="w-4 h-4 text-white" />
            </div>
          </div>
        </div>

        {/* Mobile Search Modal - Updated with platform design */}
        {mobileSearchPop && createPortal(
          <div 
            className="fixed inset-0 bg-white overflow-y-auto lg:hidden"
            style={{ 
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 99999999,
              backgroundColor: 'white'
            }}
          >
            <div className="min-h-full flex flex-col">
              {/* Header with close button */}
              <div className="flex items-center justify-between p-4 border-b border-[#e3e3e3] bg-white sticky top-0">
                <button
                  onClick={handleCloseMobileModal}
                  className="p-2 rounded-full hover:bg-gray-100 touch-manipulation"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <h2 className="text-lg font-semibold">Search</h2>
                <div className="w-10"></div>
              </div>

              {/* Content */}
              <div className="flex-1 p-4 pb-24">
                {/* Where Section */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold mb-4">Where</h3>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search destinations"
                      className="w-full p-4 border-[2px] border-[#e3e3e3] rounded-[10px] text-lg focus:outline-none focus:border-[#5b1dee] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] transition-all duration-200 ease-out"
                      value={addressInput}
                      onChange={(e) => {
                        setAddressInput(e.target.value);
                      }}
                      onFocus={(e) => {
                        if (addressInput) {
                          setTimeout(() => {
                            e.target.select();
                          }, 0);
                        }
                      }}
                      onClick={(e) => {
                        if (addressInput && e.target.selectionStart === e.target.selectionEnd) {
                          setTimeout(() => {
                            e.target.select();
                          }, 0);
                        }
                      }}
                      autoFocus
                      style={{ caretColor: '#5b1dee' }}
                    />
                    {predictList.length > 0 && (
                      <div className="absolute top-full left-0 right-0 bg-white border-[2px] border-[#e3e3e3] rounded-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] mt-1 max-h-60 overflow-y-auto z-[999999999] transition-all duration-200 ease-out">
                        {predictList.map((address) => (
                          <div
                            key={address.place_id}
                            onClick={() => {
                              console.log('Suggestion clicked:', address.description);
                              handleMobileSelectFromAddressList(address.place_id, address.description);
                            }}
                            className="p-4 hover:bg-[#f6f6f6] active:bg-gray-200 cursor-pointer border-b border-[#e3e3e3] last:border-b-0 touch-manipulation transition-colors duration-150 ease-out"
                          >
                            <div className="flex items-center gap-3">
                              <PinIcon className="flex-shrink-0" />
                              <div className="text-gray-900">{address.description}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* When Section */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold mb-4">When</h3>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <button
                      onClick={() => setShowMobileCalendar(true)}
                      className="p-4 border-[2px] border-[#e3e3e3] rounded-[10px] text-left touch-manipulation hover:bg-[#f6f6f6] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
                    >
                      <div className="text-xs text-[#666666] mb-1">CHECK-IN</div>
                      <div className="font-medium">
                        {period[0].startDate && period[0].startDate.toDateString() !== new Date().toDateString()
                          ? period[0].startDate.toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                            })
                          : "Add date"}
                      </div>
                    </button>
                    <button
                      onClick={() => setShowMobileCalendar(true)}
                      className="p-4 border-[2px] border-[#e3e3e3] rounded-[10px] text-left touch-manipulation hover:bg-[#f6f6f6] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
                    >
                      <div className="text-xs text-[#666666] mb-1">CHECK-OUT</div>
                      <div className="font-medium">
                        {period[0].endDate && period[0].endDate.toDateString() !== new Date().toDateString()
                          ? period[0].endDate.toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                            })
                          : "Add date"}
                      </div>
                    </button>
                  </div>
                </div>

                {/* Who Section */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold mb-4">Who</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-3">
                      <div>
                        <div className="font-medium">Adults</div>
                        <div className="text-sm text-[#666666]">Age 13 or above</div>
                      </div>
                      <NumberSpin
                        value={who.adults || 1}
                        onChange={(value) => dispatch(setWho({ ...who, adults: value }))}
                        min={1}
                      />
                    </div>
                    <div className="flex items-center justify-between py-3">
                      <div>
                        <div className="font-medium">Children</div>
                        <div className="text-sm text-[#666666]">Age 2-12</div>
                      </div>
                      <NumberSpin
                        value={who.children || 0}
                        onChange={(value) => dispatch(setWho({ ...who, children: value }))}
                        min={0}
                      />
                    </div>
                    <div className="flex items-center justify-between py-3">
                      <div>
                        <div className="font-medium">Infants</div>
                        <div className="text-sm text-[#666666]">Under 2</div>
                      </div>
                      <NumberSpin
                        value={who.infants || 0}
                        onChange={(value) => dispatch(setWho({ ...who, infants: value }))}
                        min={0}
                      />
                    </div>
                    <div className="flex items-center justify-between py-3">
                      <div>
                        <div className="font-medium">Pets</div>
                        <div className="text-sm text-[#666666]">Pet friendly only</div>
                      </div>
                      <NumberSpin
                        value={who.pets || 0}
                        onChange={(value) => dispatch(setWho({ ...who, pets: value }))}
                        min={0}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Fixed Search Button */}
              <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-[#e3e3e3]">
                <PurpleButton
                  text={
                    <div className="flex items-center justify-center gap-2">
                      <SearchNormalIcon className="w-5 h-5" />
                      <span>Search</span>
                    </div>
                  }
                  onClick={handleMobileSearch}
                  className="w-auto mx-auto px-8 py-4 text-lg"
                />
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Mobile Calendar Modal */}
        {showMobileCalendar && createPortal(
          <MobileCalendar
            selectedDates={{
              startDate: period[0].startDate,
              endDate: period[0].endDate
            }}
            onDateSelect={handleMobileDateSelect}
            onApply={handleMobileCalendarClose}
            onClose={handleMobileCalendarClose}
            minDate={getMinBookingDate()}
          />,
          document.body
        )}


      </div>
    </>
  );
};